import { LenderType } from 'opensolar-ui/src/checkout/checkoutFinancing/types'
import { useEffect, useMemo, useState } from 'react'
import restClient from 'restClient'
import { useDebouncedCallback } from 'util/Debounce'
import {
  ApplicationResultType,
  CheckoutFinancingApplicationResultsResponse,
  CheckoutFinancingFilterParameters,
  CheckoutFinancingFilterParametersResponse,
  CheckoutFinancingPricingOption,
  CheckoutFinancingPricingOptionsResponse,
  CheckoutFinancingPricingParams,
  CheckoutFinancingSystemDetailsResponse,
  FinanceMarketplaceSystemDetails,
} from './types'

const restClientInstance = restClient(window.API_ROOT + '/api')

export const useGetAvailableCheckoutFinanceLenders = (orgId: number, projectId: number) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [lenders, setLenders] = useState<LenderType[]>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (projectId && orgId) {
      setIsLoading(true)
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/checkout_financing/lenders/`,
      })
        .then((response) => {
          setLenders(response.data?.lenders)
        })
        .catch((err) => {
          setErrorMsg(err?.body?.message || 'An error occurred while fetching lenders')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [projectId, orgId])

  const marketPlaceLenders = useMemo(() => {
    return lenders.filter((lender) => lender.is_marketplace)
  }, [lenders])

  const referralLenders = useMemo(() => {
    return lenders.filter((lender) => !lender.is_marketplace)
  }, [lenders])

  return { isLoading, lenders, errorMsg, marketPlaceLenders, referralLenders }
}

export const useGetInitialMarketplaceFilterParameters = (
  orgId: number,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [filterParameters, setFilterParameters] = useState<CheckoutFinancingFilterParameters | null>(null)

  useEffect(() => {
    if (projectId && orgId && paymentOptionId) {
      let initialPricingParams: CheckoutFinancingPricingParams = {
        org_id: orgId,
        project_id: projectId,
        system_uuid: systemUuid,
        upfront_payment: null,
        term_start: null,
        term_end: null,
        monthly_repayment_start: null,
        monthly_repayment_end: null,
        filters: {
          sorting: 'lowest_monthly_payment',
          product_type: null,
        },
        payment_option_id: paymentOptionId,
        show_valid_only: false,
        page: 1,
        limit: 5,
      }
      setIsLoading(true)
      restClientInstance('CUSTOM_POST', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/checkout_financing/filter_parameters/`,
        data: initialPricingParams,
      })
        .then((response: CheckoutFinancingFilterParametersResponse) => {
          setFilterParameters(response?.data?.filter_parameters)
        })
        .catch((err) => {
          setErrorMsg(err?.body?.message || 'An error occurred while fetching filter parameters')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [projectId, orgId, systemUuid, paymentOptionId])

  return { isLoading, errorMsg, filterParameters }
}

export const useGetCheckoutFinancePricingOptions = (
  orgId: number,
  projectId: number,
  incomingPricingParams: CheckoutFinancingPricingParams | null
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [pricingOptions, setPricingOptions] = useState<CheckoutFinancingPricingOption[]>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [filterParameters, setFilterParameters] = useState<CheckoutFinancingFilterParameters>({
    minimum_upfront_payment: 0,
    maximum_upfront_payment: 100000,
    minimum_term: 0,
    maximum_term: 30,
    minimum_monthly_repayment: 0,
    maximum_monthly_repayment: 100000,
  })
  const [pricingParams, setPricingParams] = useState<CheckoutFinancingPricingParams | null>(incomingPricingParams)
  const [totalPricingOptions, setTotalPricingOptions] = useState<number>(0)

  const doDebouncedOnFieldChange = useDebouncedCallback((val: CheckoutFinancingPricingParams) => {
    setPricingParams(val)
  }, 300)

  useEffect(() => {
    if (incomingPricingParams) {
      if (!pricingParams) {
        setPricingParams(incomingPricingParams)
      } else if (JSON.stringify(incomingPricingParams) !== JSON.stringify(pricingParams)) {
        doDebouncedOnFieldChange(incomingPricingParams)
      }
    }
  }, [incomingPricingParams])

  useEffect(() => {
    if (projectId && orgId && pricingParams && pricingParams?.payment_option_id) {
      setIsLoading(true)
      restClientInstance('CUSTOM_POST', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/checkout_financing/pricing_options/`,
        data: pricingParams,
      })
        .then((response: CheckoutFinancingPricingOptionsResponse) => {
          setPricingOptions(response.data?.pricing_options)
          setFilterParameters(response?.data?.filter_parameters)
          setTotalPricingOptions(response.data?.total_pricing_options)
        })
        .catch((err) => {
          setErrorMsg(err?.body?.message || 'An error occurred while fetching pricing options')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [projectId, orgId, pricingParams])

  return { isLoading, pricingOptions, errorMsg, filterParameters, totalPricingOptions }
}

export const useGetCheckoutFinanceSystemDetails = (
  orgId: number,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [systemDetails, setSystemDetails] = useState<FinanceMarketplaceSystemDetails | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (projectId && orgId && systemUuid && paymentOptionId) {
      setIsLoading(true)
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/checkout_financing/system_details/?system_uuid=${systemUuid}&payment_option_id=${paymentOptionId}`,
      })
        .then((response: CheckoutFinancingSystemDetailsResponse) => {
          setSystemDetails(response.data?.system_details)
        })
        .catch((err) => {
          setErrorMsg(err?.body?.message || 'An error occurred while fetching the system details')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [projectId, orgId, systemUuid, paymentOptionId])

  return { isLoading, errorMsg, systemDetails }
}

export const useCheckoutFinanceIsEnabled = (
  orgId: number,
  projectId: number,
  disabledFinanceProductCategoryIds: number[]
) => {
  const [isEnabled, setIsEnabled] = useState(!disabledFinanceProductCategoryIds?.length)
  const { lenders } = useGetAvailableCheckoutFinanceLenders(orgId, projectId)

  useEffect(() => {
    if (lenders?.length && disabledFinanceProductCategoryIds?.length) {
      let hasEnabledCategory = false
      lenders.forEach((lender) => {
        lender.finance_product_category_ids?.forEach((categoryId) => {
          if (!disabledFinanceProductCategoryIds.includes(categoryId)) {
            hasEnabledCategory = true
          }
        })
      })
      setIsEnabled(hasEnabledCategory)
    }
  }, [lenders, disabledFinanceProductCategoryIds])

  return { isEnabled }
}

export const formatCurrencyWithSymbol = (value, currencySymbol, locale?, places?) => {
  if (currencySymbol === '€') {
    if (['en', 'en-us', 'en-gb', 'en-au'].includes(locale?.toLowerCase())) {
      // Beware language us lowercase like en-us not en-US
      return '€' + formatCurrencyNumber(value, places, locale)
    } else {
      return formatCurrencyNumber(value, places, locale) + ' €'
    }
  } else {
    return currencySymbol + formatCurrencyNumber(value, places, locale)
  }
}

export const convertLocaleName = (locale) => {
  if (!locale || !locale.includes) {
    return locale
  } else if (!locale.includes('_')) {
    return locale
  } else {
    const parts = locale.split('_')
    return `${parts[0]}-${parts[1]}`
  }
}

export const formatCurrencyNumber = (value, places, locale?: string) => {
  if (!places && places !== 0) {
    places = 2
  }
  locale = convertLocaleName(locale)

  return new Intl.NumberFormat(locale, { minimumFractionDigits: places, maximumFractionDigits: places }).format(
    value ? value : 0
  )
}

export const useGetMarketplaceApplicationResults = (orgId: number | undefined, projectId: number) => {
  const [applicationsAreLoading, setApplicationsAreLoading] = useState<boolean>(false)
  const [applicationResults, setApplicationResults] = useState<ApplicationResultType[]>([])
  const [refreshTrigger, setRefreshTrigger] = useState<string | null>(null)

  useEffect(() => {
    if (projectId && orgId) {
      setApplicationsAreLoading(true)
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/checkout_financing/application_results/`,
      })
        .then((response: CheckoutFinancingApplicationResultsResponse) => {
          setApplicationResults(response.data?.application_results)
        })
        .catch((err) => {})
        .finally(() => {
          setApplicationsAreLoading(false)
        })
    }
  }, [projectId, orgId, refreshTrigger])

  const forceRefresh = () => {
    setRefreshTrigger(Date.now().toString())
  }

  return { applicationsAreLoading, applicationResults, refreshApplications: forceRefresh }
}

export const removeCountryCodeFromPhone = (rawPhone: string | undefined) => {
  if (!rawPhone) return rawPhone
  if (rawPhone.startsWith('+44')) {
    return rawPhone.slice(3)
  }
  if (rawPhone.startsWith('(44)')) {
    return rawPhone.slice(4)
  }
  if (rawPhone.startsWith('44') && rawPhone.length >= 12) {
    return rawPhone.slice(2)
  }
  return rawPhone
}
